import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';

const categoryIcons = {
  payment_succeeded: '💰',
  payment_failed: '❌',
  payment_receipt: '🧾',
  payment_refunded: '↩️',
  charge_approval: '📋',
  charge_approved: '✅',
  charge_disputed: '⚠️',
  charge_resolved: '✅',
  charge_withdrawn: '🗑️',
  autopay_reminder: '🔔',
  walkthrough_reminder: '📅',
  lease_expiry_30d: '📆',
  lease_expiry_60d: '📆',
  lease_expiry_90d: '📆',
  payment_method_expiry: '💳',
  payment_method_expiring: '💳',
  application_comment: '💬',
  application_status: '📄',
  new_application: '📩',
  maintenance_update: '🔧',
  viewings: '👀',
  new_message: '💬',
  application_submitted: '📩'
};

function NotificationCenter() {
  const { user, authReady } = useContext(AuthContext);
  const navigate = useNavigate();

  const openNotification = (n) => {
    if (!n.is_read) markAsRead(n.id);
    if (n.cta_url) {
      try { const u = new URL(n.cta_url, window.location.origin); navigate(u.pathname); }
      catch { navigate(n.cta_url); }
    }
  };
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    if (!authReady || !user) return;
    const fetchNotifications = async () => {
      try {
        const endpoint = user.userType === 'landlord'
          ? `${process.env.REACT_APP_API_BASE_URL}/api/landlords/notifications?limit=100`
          : `${process.env.REACT_APP_API_BASE_URL}/api/tenants/notifications?limit=100`;
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (!res.ok) throw new Error('Failed to load notifications');
        const data = await res.json();
        setNotifications(data.notifications || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [authReady, user]);

  const markAsRead = async (id) => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const filtered = filter === 'unread'
    ? notifications.filter(n => !n.is_read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (!authReady || !user) return null;

  return (
    <main className="App-main">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Notifications</p>
          <h2>Notification Center {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}</h2>
          <p className="status-text">Stay on top of payments, applications, and important updates.</p>
        </div>
        <div className="ledger-actions">
          {unreadCount > 0 && (
            <button className="secondary-button" onClick={markAllRead}>Mark all read</button>
          )}
        </div>
      </div>

      <div className="tab-bar" style={{ marginBottom: '1rem' }}>
        <button className={filter === 'all' ? 'primary-button' : 'secondary-button'} onClick={() => setFilter('all')}>
          All ({notifications.length})
        </button>
        <button className={filter === 'unread' ? 'primary-button' : 'secondary-button'} onClick={() => setFilter('unread')}>
          Unread ({unreadCount})
        </button>
      </div>

      {loading ? (
        <p className="status-text">Loading notifications…</p>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : filtered.length === 0 ? (
        <div className="dashboard-section" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ fontSize: '1.5rem' }}>🔔</p>
          <p style={{ fontWeight: 600 }}>{filter === 'unread' ? 'All caught up!' : 'No notifications yet'}</p>
          <p className="status-text">
            {filter === 'unread'
              ? 'You\'ve read all your notifications.'
              : 'Notifications about payments, applications, and updates will appear here.'}
          </p>
        </div>
      ) : (
        <div>
          {filtered.map(n => (
            <div
              key={n.id}
              className={`dashboard-section ${n.is_read ? '' : 'notification-unread'}`}
              style={{
                padding: '0.75rem 1rem',
                marginBottom: '0.5rem',
                cursor: n.is_read ? 'default' : 'pointer',
                borderLeft: n.is_read ? '3px solid transparent' : '3px solid var(--accent-color, #2f6fed)',
                opacity: n.is_read ? 0.7 : 1
              }}
              onClick={() => !n.is_read && markAsRead(n.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{categoryIcons[n.category] || '📌'}</span>
                    <strong style={{ fontSize: '0.95rem' }}>{n.title || 'Notification'}</strong>
                    {!n.is_read && <span className="status-pill pending" style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}>New</span>}
                  </div>
                  <p className="status-text" style={{ margin: '0.25rem 0 0 1.5rem' }}>{n.message}</p>
                  <div style={{ marginTop: '0.5rem', marginLeft: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                    {n.cta_url && (
                      <button className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={(e) => { e.stopPropagation(); openNotification(n); }}>Open</button>
                    )}
                    {!n.is_read && (
                      <button className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}>Mark read</button>
                    )}
                  </div>
                </div>
                <span className="status-text" style={{ whiteSpace: 'nowrap', marginLeft: '1rem', fontSize: '0.75rem' }}>
                  {formatTime(n.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

export default NotificationCenter;
